/**
 * StellateNode - Geometry modifier that adds pyramids to faces
 *
 * Creates stellated polyhedra by adding an apex point above each face
 * and replacing the face with triangles connecting to the apex.
 * Detects coplanar adjacent triangles and merges them into polygons first.
 */

import type { BufferGeometry } from 'three';
import { BaseNode } from '@nodes/BaseNode';
import type { NodeDef } from '@runtime/IRuntime';
import type { ThreeDependencies } from '../../threejs/threejsPlugin';
import { internals, params } from '@/nodes/TypedEndpoints';

const stellateEndpoints = {
  ...params({
    height: { value: 0.5, min: -0.5, max: 1.5, step: 0.01, label: 'Height' },
  }),
  ...internals({
    geometryIn: { dataType: 'three:geometry', isInput: true, label: 'Geometry' },
    geometryOut: { dataType: 'three:geometry', isOutput: true }
  })

};

interface Vertex {
  x: number;
  y: number;
  z: number;
}

interface Triangle {
  verts: [Vertex, Vertex, Vertex];
  normal: Vertex;
  edges: [string, string, string]; // edge keys for adjacency
}

class StellateNode extends BaseNode<typeof stellateEndpoints> {
  private three: any;
  private inputGeometry: BufferGeometry | null = null;
  private outputGeometry: BufferGeometry | null = null;

  constructor(id: string, deps: ThreeDependencies) {
    super(id, deps);
    this.three = deps.three;
  }

  onInternalChanged(payload: any, endpointId: string): void {
    if (endpointId === 'geometryIn') {
      this.inputGeometry = payload?.value;
      this.stellate();
    }
  }

  onParamChanged(_payload: any, endpointId: string): void {
    if (endpointId === 'height') {
      this.stellate();
    }
  }

  private vertexKey(v: Vertex): string {
    return `${v.x.toFixed(5)},${v.y.toFixed(5)},${v.z.toFixed(5)}`;
  }

  private edgeKey(v1: Vertex, v2: Vertex): string {
    const k1 = this.vertexKey(v1);
    const k2 = this.vertexKey(v2);
    return k1 < k2 ? `${k1}|${k2}` : `${k2}|${k1}`;
  }

  private normalsMatch(n1: Vertex, n2: Vertex): boolean {
    // Use dot product to compare normals (more robust than string comparison)
    const dot = n1.x * n2.x + n1.y * n2.y + n1.z * n2.z;
    // Normals are unit vectors, so dot product of 1 means identical
    // Use tolerance for floating-point comparison
    return dot > 0.9999;
  }

  private stellate(): void {
    if (!this.inputGeometry || !this.three) return;

    if (this.outputGeometry) {
      this.outputGeometry.dispose();
    }

    const height = this.params.height.value;
    const posAttr = this.inputGeometry.getAttribute('position');
    if (!posAttr) return;

    // Extract triangles with normals
    const triangles: Triangle[] = [];
    const index = this.inputGeometry.getIndex();

    if (index) {
      // Indexed geometry
      const faceCount = index.count / 3;
      for (let i = 0; i < faceCount; i++) {
        const i0 = index.getX(i * 3);
        const i1 = index.getX(i * 3 + 1);
        const i2 = index.getX(i * 3 + 2);

        const v0: Vertex = { x: posAttr.getX(i0), y: posAttr.getY(i0), z: posAttr.getZ(i0) };
        const v1: Vertex = { x: posAttr.getX(i1), y: posAttr.getY(i1), z: posAttr.getZ(i1) };
        const v2: Vertex = { x: posAttr.getX(i2), y: posAttr.getY(i2), z: posAttr.getZ(i2) };

        const edge1 = { x: v1.x - v0.x, y: v1.y - v0.y, z: v1.z - v0.z };
        const edge2 = { x: v2.x - v0.x, y: v2.y - v0.y, z: v2.z - v0.z };
        const normal = this.normalize(this.cross(edge1, edge2));

        triangles.push({
          verts: [v0, v1, v2],
          normal,
          edges: [this.edgeKey(v0, v1), this.edgeKey(v1, v2), this.edgeKey(v2, v0)]
        });
      }
    } else {
      // Non-indexed geometry
      const faceCount = posAttr.count / 3;
      for (let i = 0; i < faceCount; i++) {
        const v0: Vertex = {
          x: posAttr.getX(i * 3),
          y: posAttr.getY(i * 3),
          z: posAttr.getZ(i * 3)
        };
        const v1: Vertex = {
          x: posAttr.getX(i * 3 + 1),
          y: posAttr.getY(i * 3 + 1),
          z: posAttr.getZ(i * 3 + 1)
        };
        const v2: Vertex = {
          x: posAttr.getX(i * 3 + 2),
          y: posAttr.getY(i * 3 + 2),
          z: posAttr.getZ(i * 3 + 2)
        };

        const edge1 = { x: v1.x - v0.x, y: v1.y - v0.y, z: v1.z - v0.z };
        const edge2 = { x: v2.x - v0.x, y: v2.y - v0.y, z: v2.z - v0.z };
        const normal = this.normalize(this.cross(edge1, edge2));

        triangles.push({
          verts: [v0, v1, v2],
          normal,
          edges: [this.edgeKey(v0, v1), this.edgeKey(v1, v2), this.edgeKey(v2, v0)]
        });
      }
    }

    // Group coplanar adjacent triangles into faces
    const faces = this.mergeCoplanarTriangles(triangles);

    // Stellate each face
    const newPositions: number[] = [];

    for (const face of faces) {
      // Calculate face centroid
      const centroid: Vertex = { x: 0, y: 0, z: 0 };
      for (const v of face.vertices) {
        centroid.x += v.x;
        centroid.y += v.y;
        centroid.z += v.z;
      }
      centroid.x /= face.vertices.length;
      centroid.y /= face.vertices.length;
      centroid.z /= face.vertices.length;

      // Create apex
      const apex: Vertex = {
        x: centroid.x + face.normal.x * height,
        y: centroid.y + face.normal.y * height,
        z: centroid.z + face.normal.z * height
      };

      // Create triangles from each edge to apex
      const verts = face.vertices;
      for (let i = 0; i < verts.length; i++) {
        const v1 = verts[i];
        const v2 = verts[(i + 1) % verts.length];

        newPositions.push(v1.x, v1.y, v1.z);
        newPositions.push(v2.x, v2.y, v2.z);
        newPositions.push(apex.x, apex.y, apex.z);
      }
    }

    this.outputGeometry = new this.three.BufferGeometry();
    this.outputGeometry.setAttribute(
      'position',
      new this.three.Float32BufferAttribute(newPositions, 3)
    );
    this.outputGeometry.computeVertexNormals();

    this.internals.geometryOut.set(this.outputGeometry);
  }

  /**
   * Merge coplanar adjacent triangles into polygon faces
   */
  private mergeCoplanarTriangles(triangles: Triangle[]): { vertices: Vertex[]; normal: Vertex }[] {
    // Build edge to triangle mapping
    const edgeToTriangles = new Map<string, number[]>();
    for (let i = 0; i < triangles.length; i++) {
      for (const edge of triangles[i].edges) {
        if (!edgeToTriangles.has(edge)) edgeToTriangles.set(edge, []);
        edgeToTriangles.get(edge)!.push(i);
      }
    }

    // Find coplanar neighbors
    const coplanarNeighbors = (ti: number): number[] => {
      const t = triangles[ti];
      const neighbors: number[] = [];
      for (const edge of t.edges) {
        const adj = edgeToTriangles.get(edge) || [];
        for (const ni of adj) {
          if (ni !== ti && this.normalsMatch(triangles[ni].normal, t.normal)) {
            neighbors.push(ni);
          }
        }
      }
      return neighbors;
    };

    // Group triangles into coplanar connected components
    const visited = new Set<number>();
    const groups: number[][] = [];

    for (let i = 0; i < triangles.length; i++) {
      if (visited.has(i)) continue;

      const group: number[] = [];
      const queue = [i];
      visited.add(i);

      while (queue.length > 0) {
        const ti = queue.shift()!;
        group.push(ti);

        for (const ni of coplanarNeighbors(ti)) {
          if (!visited.has(ni)) {
            visited.add(ni);
            queue.push(ni);
          }
        }
      }

      groups.push(group);
    }

    // Convert each group to a polygon by finding the boundary edges
    const faces: { vertices: Vertex[]; normal: Vertex }[] = [];

    for (const group of groups) {
      if (group.length === 1) {
        // Single triangle - use as is
        const t = triangles[group[0]];
        faces.push({ vertices: [...t.verts], normal: t.normal });
        continue;
      }

      // Find boundary edges (edges that appear only once in the group)
      const edgeCount = new Map<string, number>();
      const edgeVerts = new Map<string, [Vertex, Vertex]>();

      for (const ti of group) {
        const t = triangles[ti];
        for (let i = 0; i < 3; i++) {
          const edge = t.edges[i];
          edgeCount.set(edge, (edgeCount.get(edge) || 0) + 1);
          if (!edgeVerts.has(edge)) {
            edgeVerts.set(edge, [t.verts[i], t.verts[(i + 1) % 3]]);
          }
        }
      }

      // Boundary edges appear exactly once
      const boundaryEdges: [Vertex, Vertex][] = [];
      for (const [edge, count] of edgeCount) {
        if (count === 1) {
          boundaryEdges.push(edgeVerts.get(edge)!);
        }
      }

      // Chain boundary edges into a polygon
      const expectedNormal = triangles[group[0]].normal;
      const polygon = this.chainEdges(boundaryEdges, expectedNormal);
      if (polygon.length >= 3) {
        faces.push({ vertices: polygon, normal: expectedNormal });
      }
    }

    return faces;
  }

  /**
   * Chain edges into an ordered polygon with correct winding
   */
  private chainEdges(edges: [Vertex, Vertex][], expectedNormal: Vertex): Vertex[] {
    if (edges.length === 0) return [];

    const polygon: Vertex[] = [];
    const remaining = [...edges];

    // Start with first edge
    let [current, next] = remaining.shift()!;
    polygon.push(current);

    while (remaining.length > 0) {
      polygon.push(next);
      const nextKey = this.vertexKey(next);

      // Find edge that starts or ends with 'next'
      let found = false;
      for (let i = 0; i < remaining.length; i++) {
        const [a, b] = remaining[i];
        if (this.vertexKey(a) === nextKey) {
          next = b;
          remaining.splice(i, 1);
          found = true;
          break;
        } else if (this.vertexKey(b) === nextKey) {
          next = a;
          remaining.splice(i, 1);
          found = true;
          break;
        }
      }

      if (!found) break;
    }

    // Verify winding by computing polygon normal and comparing to expected
    if (polygon.length >= 3) {
      const computedNormal = this.computePolygonNormal(polygon);
      const dot = computedNormal.x * expectedNormal.x +
        computedNormal.y * expectedNormal.y +
        computedNormal.z * expectedNormal.z;

      // If normals point opposite directions, reverse the polygon
      if (dot < 0) {
        polygon.reverse();
      }
    }

    return polygon;
  }

  /**
   * Compute the normal of a polygon using Newell's method
   */
  private computePolygonNormal(vertices: Vertex[]): Vertex {
    const normal: Vertex = { x: 0, y: 0, z: 0 };
    const n = vertices.length;

    for (let i = 0; i < n; i++) {
      const current = vertices[i];
      const next = vertices[(i + 1) % n];

      normal.x += (current.y - next.y) * (current.z + next.z);
      normal.y += (current.z - next.z) * (current.x + next.x);
      normal.z += (current.x - next.x) * (current.y + next.y);
    }

    return this.normalize(normal);
  }

  private cross(a: Vertex, b: Vertex): Vertex {
    return {
      x: a.y * b.z - a.z * b.y,
      y: a.z * b.x - a.x * b.z,
      z: a.x * b.y - a.y * b.x
    };
  }

  private normalize(v: Vertex): Vertex {
    const len = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
    if (len === 0) return { x: 0, y: 1, z: 0 };
    return { x: v.x / len, y: v.y / len, z: v.z / len };
  }

  dispose(): void {
    if (this.outputGeometry) {
      this.outputGeometry.dispose();
      this.outputGeometry = null;
    }
    this.inputGeometry = null;
    super.dispose();
  }
}

export const stellateNodeDef: NodeDef<StellateNode> = {
  type: 'Stellate',
  shortName: 'stellate',
  info: 'Adds pyramids to each face, creating stellated polyhedra. Merges coplanar triangles first.',
  category: 'modifier',
  endpoints: stellateEndpoints,
  ctor: StellateNode
};
